import { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { usePlatformStatus } from '@/contexts/PlatformStatusContext';
import { supabase } from '@/integrations/supabase/client';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ForgotPasswordForm } from '@/components/ForgotPasswordForm';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { z } from 'zod';

const emailSchema = z.string().email('Please enter a valid email');

const strongPasswordSchema = z.string()
  .min(8, 'Password must be at least 8 characters')
  .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
  .regex(/[0-9]/, 'Password must contain at least one number')
  .regex(/[^A-Za-z0-9]/, 'Password must contain at least one special character');

const loginPasswordSchema = z.string().min(1, 'Password is required');

export default function Auth() {
  const [searchParams] = useSearchParams();
  const refParam = searchParams.get('ref') || '';

  const [isLogin, setIsLogin] = useState(!refParam);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [fullName, setFullName] = useState('');
  const [referralCode, setReferralCode] = useState(refParam);
  const [showPassword, setShowPassword] = useState(false);
  const [newsletterOptIn, setNewsletterOptIn] = useState(true);
  const [loading, setLoading] = useState(false);
  const [comingSoon, setComingSoon] = useState<boolean | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);

  const { signIn, signUp, signOut, user } = useAuth();
  const { maintenanceMode, loading: statusLoading } = usePlatformStatus();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/dashboard';

  // Read coming_soon_mode and the user's admin role so we don't auto-redirect
  // a non-admin into /dashboard, which would just render the waiting-list
  // takeover and trap them with no way to sign out.
  useEffect(() => {
    let active = true;
    supabase
      .from('admin_settings')
      .select('value')
      .eq('key', 'coming_soon_mode')
      .maybeSingle()
      .then(({ data }) => { if (active) setComingSoon(data?.value === 'true'); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    if (!user) { setIsAdmin(false); return; }
    supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', user.id)
      .eq('role', 'admin')
      .maybeSingle()
      .then(({ data }) => { if (active) setIsAdmin(!!data); });
    return () => { active = false; };
  }, [user]);

  // If logged in and arrived with a referral code, link it to their profile
  if (user && refParam) {
    supabase
      .from('profiles')
      .update({ referred_by_code: refParam })
      .eq('id', user.id)
      .then(() => {
        toast.success('Referral code linked to your account!');
        navigate(from, { replace: true });
      });
    return null;
  }

  // Wait until we know coming-soon + role before deciding what to render.
  if (user && (comingSoon === null || isAdmin === null || statusLoading)) {
    return <div className="min-h-screen bg-background" />;
  }

  // Non-admin signed in during takeover → DON'T redirect (they'd land on the
  // waiting-list page with no way out). Show a sign-out panel instead.
  if (user && comingSoon && !isAdmin) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-card border border-border rounded-lg p-8 text-center space-y-5">
          <h1 className="text-3xl font-serif tracking-[0.2em]">ØUTLAWS</h1>
          <div className="h-px w-12 bg-primary mx-auto" />
          <p className="text-sm text-muted-foreground">You're signed in as</p>
          <p className="text-foreground font-medium break-all">{user.email}</p>
          <p className="text-sm text-muted-foreground">
            The site is currently in coming-soon mode. Sign out to switch accounts or return to the waiting list.
          </p>
          <div className="flex flex-col gap-2 pt-2">
            <Button
              onClick={async () => { await signOut(); }}
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground tracking-widest uppercase h-11"
            >
              Sign out
            </Button>
            <Link
              to="/"
              className="text-xs text-muted-foreground hover:text-primary transition-colors pt-2"
            >
              Back to waiting list
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Otherwise (admin, or coming-soon off) → normal redirect behavior.
  if (user && !statusLoading && !maintenanceMode) {
    navigate(from, { replace: true });
    return null;
  }

  const validateForm = () => {
    try {
      emailSchema.parse(email);
      if (isLogin) {
        loginPasswordSchema.parse(password);
      } else {
        strongPasswordSchema.parse(password);
        if (!fullName.trim()) {
          toast.error('Please enter your first name');
          return false;
        }
      }
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast.error(error.errors[0].message);
      }
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);

    try {
      if (isLogin) {
        const { error } = await signIn(email, password);
        if (error) {
          if (error.message.includes('Invalid login credentials')) {
            toast.error('Invalid email or password');
          } else {
            toast.error(error.message);
          }
        } else {
          toast.success('Welcome back!');
          navigate(from, { replace: true });
        }
      } else {
        const codeToUse = referralCode.trim();
        const { error } = await signUp(email, password, fullName, phone.trim() || undefined, codeToUse || undefined);
        if (error) {
          if (error.message.includes('already registered')) {
            toast.error('This email is already registered. Please sign in.');
          } else {
            toast.error(error.message);
          }
        } else {
          // Newsletter opt-in (best effort — never block signup)
          if (newsletterOptIn) {
            try {
              const { supabase } = await import('@/integrations/supabase/client');
              await supabase.from('newsletter_subscribers').insert({
                email: email.toLowerCase().trim(),
                full_name: fullName.trim() || null,
                source: 'signup',
              });
            } catch (e) {
              console.warn('Newsletter subscribe failed (non-fatal):', e);
            }
          }
          toast.success('Account created successfully!');
          navigate(from, { replace: true });
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left Side - Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 md:px-16 lg:px-24">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-12 w-fit"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </button>

        <div className="max-w-md w-full mx-auto lg:mx-0">
          <div className="mb-8">
            <Logo size="md" />
          </div>

          {showForgotPassword ? (
            <ForgotPasswordForm onBack={() => setShowForgotPassword(false)} />
          ) : (
            <>
              <h1 className="font-display text-3xl md:text-4xl italic mb-2">
                {isLogin ? 'Welcome Back' : 'Join the Crew'}
              </h1>
              <p className="text-muted-foreground mb-8">
                {isLogin
                  ? 'Sign in to access your exclusive dashboard'
                  : 'Create your account and become an Øutlaw'}
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                {!isLogin && (
                  <div className="space-y-2">
                    <Label htmlFor="fullName">First Name</Label>
                    <Input
                      id="fullName"
                      type="text"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder="Enter your first name"
                      className="bg-card border-border"
                    />
                    <p className="text-xs text-muted-foreground">This is how we'll address you</p>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className="bg-card border-border"
                  />
                  <p className="text-xs text-muted-foreground">We'll use this for order updates and account recovery</p>
                </div>

                {!isLogin && (
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="Enter your phone number"
                      className="bg-card border-border"
                    />
                    <p className="text-xs text-muted-foreground">We'll use this for delivery updates</p>
                  </div>
                )}

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Password</Label>
                    {isLogin && (
                      <button
                        type="button"
                        onClick={() => setShowForgotPassword(true)}
                        className="text-xs text-primary hover:underline"
                      >
                        Forgot password?
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={isLogin ? 'Enter your password' : 'Create a strong password'}
                      className="bg-card border-border pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {!isLogin && (
                    <p className="text-xs text-muted-foreground">
                      Min 8 characters with uppercase, lowercase, number and special character
                    </p>
                  )}
                </div>

                {/* Referral code field — always visible on signup */}
                {!isLogin && (
                  <div className="space-y-2">
                    <Label htmlFor="referral">Referral Code</Label>
                    <Input
                      id="referral"
                      type="text"
                      value={referralCode}
                      onChange={(e) => !refParam && setReferralCode(e.target.value)}
                      readOnly={Boolean(refParam)}
                      placeholder="Enter referral code (optional)"
                      className={`bg-card border-border ${refParam ? 'text-muted-foreground cursor-not-allowed' : ''}`}
                    />
                    <p className="text-xs text-muted-foreground">
                      {refParam ? 'This code links you to a partner' : 'Have a referral code? Enter it here'}
                    </p>
                  </div>
                )}

                {!isLogin && (
                  <label className="flex items-start gap-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={newsletterOptIn}
                      onChange={(e) => setNewsletterOptIn(e.target.checked)}
                      className="mt-1 w-4 h-4 accent-primary cursor-pointer"
                      aria-label="Subscribe to newsletter"
                    />
                    <span className="text-sm text-muted-foreground">
                      Subscribe to news, drops & event updates from ØUTLAWS. Unsubscribe anytime.
                    </span>
                  </label>
                )}

                <Button
                  type="submit"
                  className="w-full btn-racing-filled"
                  disabled={loading}
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                  ) : isLogin ? (
                    'Sign In'
                  ) : (
                    'Create Account'
                  )}
                </Button>
              </form>

              <p className="mt-6 text-center text-muted-foreground">
                {isLogin ? "Don't have an account? " : 'Already have an account? '}
                <button
                  type="button"
                  onClick={() => setIsLogin(!isLogin)}
                  className="text-primary hover:underline"
                >
                  {isLogin ? 'Sign Up' : 'Sign In'}
                </button>
              </p>
            </>
          )}
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div className="absolute inset-0 bg-gradient-to-r from-background to-transparent z-10" />
        <img loading="lazy" decoding="async"
          src="/lovable-uploads/374b2d4c-9929-4020-b814-91c5911b3d1a.webp"
          alt="Racing"
          className="w-full h-full object-cover"
        />
        <div className="absolute bottom-12 left-12 z-20 max-w-md" />
      </div>
    </div>
  );
}
